import { AxiosError } from 'axios';
import { FormikProps, FormikValues } from 'formik';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { api } from '../../hooks/api.hooks';
import { ITranslations } from '../../i18n/config';
import { useMessage } from '../../state/Message';
import { setMessage } from '../../state/Message/actions';
import { FormHelpers } from '../Form/Form';

export const usePhoneForm = (
  form: React.RefObject<FormikProps<FormikValues>>,
  user: Api.User,
  copyIdentifier: 'add' | 'edit',
  redirectUrl: string,
) => {
  const { dispatch: dispatchMessage } = useMessage();
  const history = useHistory();

  const sendCode = async ({ phoneNumber }: any, { setFieldError }: FormHelpers) => {
    try {
      await api.post('/access-keys/phone', {
        phoneNumber,
      });
    } catch (e) {
      setFieldError('phone', 'error.server.unknown');
      throw e;
    }
  };

  const resendCode = async () => {
    try {
      await api.post('/access-keys/phone/resend');
      dispatchMessage(
        setMessage('accessKeys.phone.form.step2.resent', 'accessKeysPhone', 'success', {
          phone: form?.current?.values.phoneNumber || user.phone?.candidate,
        }),
      );
    } catch (e) {
      const { response } = e as AxiosError;
      if (response?.status === 429) {
        form?.current?.getFieldHelpers('code').setError('error.code.too_many_attempts');
        return;
      }

      form?.current?.getFieldHelpers('code').setError('error.server.unknown');
    }
  };

  const handleSubmit = async ({ code }: any, { setFieldError, enableValidation }: FormHelpers) => {
    try {
      await api.post('/access-keys/phone/verify', {
        code,
      });
      dispatchMessage(
        setMessage(
          `accessKeys.phone.${copyIdentifier}.success` as ITranslations,
          'accessKeysOverview',
          'success',
        ),
      );
      if (redirectUrl && redirectUrl !== '') {
        window.location.href = `${redirectUrl}?success=true`;
      } else {
        history.push('/gegevens');
      }
    } catch (e) {
      const { response } = e as AxiosError;
      if (response?.status === 401 || response?.status === 400) {
        setFieldError('code', 'error.code.invalid');
        enableValidation(false);
        return;
      }

      if (response?.status === 429) {
        setFieldError('code', 'error.code.too_many_attempts');
        enableValidation(false);
        return;
      }

      setFieldError('code', 'error.server.unknown');

      // Handle other errors
      if (redirectUrl && redirectUrl !== '') {
        window.location.href = `${redirectUrl}?success=false`;
      } else {
        history.push('/gegevens?success=false');
      }
    }
  };

  const stopProgress = async () => {
    await api.post('/access-keys/phone/cancel');

    if (redirectUrl && redirectUrl !== '') {
      window.location.href = `${redirectUrl}?success=false`;
    } else {
      history.push('/gegevens');
    }
  };

  return {
    sendCode,
    resendCode,
    handleSubmit,
    stopProgress,
  };
};
